// Explore-stats page: Referendums + plebiscites since Federation (#224).
// Sourced from the `referendums` index (Parliamentary Handbook API). National
// and per-state Yes% are computed from the per-division Results. Flagship:
// ranked, sortable table of every proposal (question, Yes%, states carried,
// result). Slices: success-rate trend by decade, by type, and average Yes% by
// state.

import React, { useState, useEffect } from 'react';
import { Paper, Typography, Grid } from '@mui/material';
import { getExploreStat } from '../../services/api';
import ExploreStatsPage from './ExploreStatsPage';
import { StatBar, StatStackedBar } from './charts';

export default function Referendums() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getExploreStat('referendums');
        if (active) setData(res);
      } catch (e) {
        if (active) setError(e.message || 'Failed to load statistics');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const charts = data?.charts || {};

  return (
    <ExploreStatsPage
      title="Referendums + plebiscites"
      description={`Every constitutional referendum, plebiscite and national survey since Federation${
        charts.total ? ` (${charts.total}, of which ${charts.carried} carried)` : ''
      } — question, national Yes%, states carried, and result. Yes% is computed from the per-division vote counts.`}
      loading={loading}
      error={error}
      columns={data?.columns || []}
      rows={data?.table_rows || []}
      links={data?._links || []}
      csvName="referendums"
    >
      {data && (
        <>
          <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>Outcomes by decade</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Carried vs not carried — the long story of constitutional change being hard to pass.
            </Typography>
            <StatStackedBar data={charts.by_decade || []} xKey="decade"
                            keys={['Carried', 'Not carried']} height={320} />
          </Paper>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>By type</Typography>
                <StatStackedBar data={charts.by_type || []} xKey="type"
                                keys={['Carried', 'Not carried']} height={300} />
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 3 }}>
                <Typography variant="h6" sx={{ mb: 1 }}>Average Yes% by State</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Mean Yes vote across all referendums — which states are most receptive.
                </Typography>
                <StatBar data={charts.by_state || []} xKey="state" yKey="yes_pct"
                         layout="vertical" height={300} />
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </ExploreStatsPage>
  );
}
